import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Button, Dropdown, Grid, Icon, Form, Input, Modal, Header, List } from "semantic-ui-react";
import axios from "axios";
import { buildApiUrl } from "../utils/apiConfig";
import { useMapContext } from "./MapContext";
import SidebarWrapper from './SidebarWrapper';

const SUPPORTED_TRANSPORT_OPTIONS = [
  { key: "road", text: "Road", value: "road", icon: "car" },
  { key: "river", text: "River", value: "river", icon: "ship" },
  { key: "sea", text: "Sea", value: "sea", icon: "anchor" },
];

// MobileTourUserView renders port photos across the card width with a 142px header height.
// At the standard 390px mobile viewport minus 10px horizontal content padding, this is the practical target ratio.
const PORT_CARD_PHOTO_ASPECT_RATIO = 370 / 142;
const PORT_CARD_PHOTO_OUTPUT_WIDTH = 1200;
const PORT_CARD_PHOTO_OUTPUT_HEIGHT = Math.round(PORT_CARD_PHOTO_OUTPUT_WIDTH / PORT_CARD_PHOTO_ASPECT_RATIO);

const normalizeSupportedTransportModes = (modes) => {
  const normalized = Array.isArray(modes)
    ? modes
      .map((mode) => String(mode || "").trim().toLowerCase())
      .filter((mode) => ["road", "river", "sea"].includes(mode))
    : [];
  return normalized.length ? Array.from(new Set(normalized)) : ["road"];
};

// Helper to convert [lat, lon] to [lon, lat]
const llToLonLat = (coord) => {
  if (!Array.isArray(coord) || coord.length < 2) return null;
  return [coord[1], coord[0]];
};

const getCropBaseDimensions = (imageMeta, frameSize) => {
  if (!imageMeta?.width || !imageMeta?.height || !frameSize?.width || !frameSize?.height) {
    return { width: 0, height: 0 };
  }

  const imageRatio = imageMeta.width / imageMeta.height;
  const frameRatio = frameSize.width / frameSize.height;

  if (imageRatio > frameRatio) {
    return {
      width: frameSize.height * imageRatio,
      height: frameSize.height,
    };
  }

  return {
    width: frameSize.width,
    height: frameSize.width / imageRatio,
  };
};

const clampCropOffset = (offset, imageMeta, frameSize, zoom) => {
  const baseDimensions = getCropBaseDimensions(imageMeta, frameSize);
  if (!baseDimensions.width || !baseDimensions.height) return { x: 0, y: 0 };

  const displayWidth = baseDimensions.width * zoom;
  const displayHeight = baseDimensions.height * zoom;
  const maxX = Math.max(0, (displayWidth - frameSize.width) / 2);
  const maxY = Math.max(0, (displayHeight - frameSize.height) / 2);

  return {
    x: Math.min(maxX, Math.max(-maxX, offset.x || 0)),
    y: Math.min(maxY, Math.max(-maxY, offset.y || 0)),
  };
};

export default function PortDockEditBar({
  showPortDockEditBar,
  setShowPortDockEditBar,
  getApiHeaders,
  activeUser,
  isMobile,
  sidebarConfig,
  onHeightChange, // Add callback for height changes
  externalDockEditRequest,
  onExternalDockEditRequestHandled,
  onPortCreated,
  onDockCreated,
}) {
  const mapCtx = useMapContext();
  const handledExternalDockEditRequestRef = useRef(null);

  // Mode state
  const [editMode, setEditMode] = useState(null); // 'port' | 'dock' | null
  const [isEditingPort, setIsEditingPort] = useState(false);
  const [editingPortId, setEditingPortId] = useState(null);
  const [isEditingDock, setIsEditingDock] = useState(false);
  const [editingDockId, setEditingDockId] = useState(null);

  // Port creation state
  const [newPortName, setNewPortName] = useState("");
  const [newPortCountry, setNewPortCountry] = useState("");
  const [newPortTimezone, setNewPortTimezone] = useState("Europe/Vienna");
  const [newPortSupportedTransportModes, setNewPortSupportedTransportModes] = useState(["road"]);
  const [newPortPhotoFile, setNewPortPhotoFile] = useState(null);
  const [newPortPhotoPreviewUrl, setNewPortPhotoPreviewUrl] = useState("");
  const [existingPortPhotoUrl, setExistingPortPhotoUrl] = useState("");
  const [countries, setCountries] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [isLookingUpCoordinates, setIsLookingUpCoordinates] = useState(false);
  const [nearbyPorts, setNearbyPorts] = useState([]);
  const [showNearbyPortsModal, setShowNearbyPortsModal] = useState(false);
  const [tempLonLat, setTempLonLat] = useState(null);

  // Docking location state
  const [newDockName, setNewDockName] = useState("");
  const [newDockPort, setNewDockPort] = useState("");
  const [newDockType, setNewDockType] = useState("");
  const [newDockRiverKm, setNewDockRiverKm] = useState("");
  const [newDockNotes, setNewDockNotes] = useState("");
  const [availablePorts, setAvailablePorts] = useState([]);
  const [dockingTypes, setDockingTypes] = useState([]);
  const availablePortsLoadedRef = useRef(false);
  const dockingTypesLoadedRef = useRef(false);

  // Existing ports for display/editing
  const [allPorts, setAllPorts] = useState([]);
  const portPhotoPreviewObjectUrlRef = useRef(null);
  const portPhotoCropObjectUrlRef = useRef(null);
  const portPhotoFileInputRef = useRef(null);
  const portPhotoCropFrameRef = useRef(null);
  const portPhotoCropDragRef = useRef(null);
  const [portPhotoCropModalOpen, setPortPhotoCropModalOpen] = useState(false);
  const [portPhotoCropFile, setPortPhotoCropFile] = useState(null);
  const [portPhotoCropSourceUrl, setPortPhotoCropSourceUrl] = useState("");
  const [portPhotoCropImageMeta, setPortPhotoCropImageMeta] = useState(null);
  const [portPhotoCropFrameSize, setPortPhotoCropFrameSize] = useState({ width: 0, height: 0 });
  const [portPhotoCropZoom, setPortPhotoCropZoom] = useState(1);
  const [portPhotoCropOffset, setPortPhotoCropOffset] = useState({ x: 0, y: 0 });

  // Feedback state
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackType, setFeedbackType] = useState("success");
  const [isSaving, setIsSaving] = useState(false);
  const capabilityCodes = useMemo(
    () => (Array.isArray(activeUser?.capabilities)
      ? activeUser.capabilities
      : (Array.isArray(activeUser?.subscription?.capabilities) ? activeUser.subscription.capabilities : [])),
    [activeUser?.capabilities, activeUser?.subscription?.capabilities]
  );
  const capabilitySet = useMemo(() => new Set(capabilityCodes), [capabilityCodes]);
  const hasCapabilityList = capabilityCodes.length > 0;
  const hasCapability = useCallback((codes, legacyValue = false) => {
    const normalizedCodes = Array.isArray(codes) ? codes : [codes];
    if (capabilitySet.has("*")) return true;
    if (normalizedCodes.some((code) => capabilitySet.has(code))) return true;
    return !hasCapabilityList && Boolean(legacyValue);
  }, [capabilitySet, hasCapabilityList]);
  const canViewPorts = useMemo(
    () => hasCapability("ports.view", activeUser?.subscription?.can_view_ports_docks),
    [activeUser?.subscription?.can_view_ports_docks, hasCapability]
  );
  const canEditPorts = useMemo(
    () => hasCapability("ports.edit", activeUser?.subscription?.can_edit_ports_docks),
    [activeUser?.subscription?.can_edit_ports_docks, hasCapability]
  );
  const canViewDocks = useMemo(
    () => hasCapability(
      "docks.view",
      activeUser?.subscription?.can_view_ports_docks ?? activeUser?.subscription?.can_view_locks
    ),
    [activeUser?.subscription?.can_view_locks, activeUser?.subscription?.can_view_ports_docks, hasCapability]
  );
  const canEditDocks = useMemo(
    () => hasCapability("docks.edit", activeUser?.subscription?.can_edit_ports_docks),
    [activeUser?.subscription?.can_edit_ports_docks, hasCapability]
  );
  const canViewPortsDocks = canViewPorts || canViewDocks;
  const canEditPortsDocks = canEditPorts || canEditDocks;
  const countryOptions = useMemo(() => {
    const seen = new Set();
    return countries
      .filter((country) => country?.iso3_code)
      .filter((country) => {
        if (seen.has(country.iso3_code)) return false;
        seen.add(country.iso3_code);
        return true;
      })
      .map((country) => ({
        key: country.iso3_code,
        text: country.name,
        value: country.iso3_code,
      }));
  }, [countries]);
  const timezoneOptions = useMemo(() => {
    const seen = new Set();
    return timezones
      .filter((timezone) => typeof timezone === "string" && timezone.trim())
      .filter((timezone) => {
        if (seen.has(timezone)) return false;
        seen.add(timezone);
        return true;
      })
      .map((timezone) => ({
        key: timezone,
        text: timezone,
        value: timezone,
      }));
  }, [timezones]);
  const availablePortOptions = useMemo(() => {
    const seen = new Set();
    return availablePorts
      .filter((port) => port?.id != null)
      .filter((port) => {
        if (seen.has(port.id)) return false;
        seen.add(port.id);
        return true;
      })
      .map((port) => ({
        key: port.id,
        text: port.name,
        value: port.id,
      }));
  }, [availablePorts]);
  const dockingTypeOptions = useMemo(() => {
    const seen = new Set();
    return dockingTypes
      .filter((dockingType) => dockingType?.id != null)
      .filter((dockingType) => {
        if (seen.has(dockingType.id)) return false;
        seen.add(dockingType.id);
        return true;
      })
      .map((dockingType) => ({
        key: dockingType.id,
        text: dockingType.name,
        value: dockingType.id,
      }));
  }, [dockingTypes]);

  const setTempPointPosition = useCallback((lonlat) => {
    if (Array.isArray(lonlat) && lonlat.length >= 2) {
      setTempLonLat([Number(lonlat[0]), Number(lonlat[1])]);
      return;
    }
    setTempLonLat(null);
  }, []);

  const clearPortPhotoPreview = useCallback(() => {
    if (portPhotoPreviewObjectUrlRef.current) {
      URL.revokeObjectURL(portPhotoPreviewObjectUrlRef.current);
      portPhotoPreviewObjectUrlRef.current = null;
    }
    setNewPortPhotoPreviewUrl("");
  }, []);

  const clearPortPhotoCropSource = useCallback(() => {
    if (portPhotoCropObjectUrlRef.current) {
      URL.revokeObjectURL(portPhotoCropObjectUrlRef.current);
      portPhotoCropObjectUrlRef.current = null;
    }
    setPortPhotoCropSourceUrl("");
  }, []);

  const closePortPhotoCropModal = useCallback(() => {
    setPortPhotoCropModalOpen(false);
    setPortPhotoCropFile(null);
    setPortPhotoCropImageMeta(null);
    setPortPhotoCropZoom(1);
    setPortPhotoCropOffset({ x: 0, y: 0 });
    portPhotoCropDragRef.current = null;
    clearPortPhotoCropSource();
    if (portPhotoFileInputRef.current) {
      portPhotoFileInputRef.current.value = "";
    }
  }, [clearPortPhotoCropSource]);

  const setCroppedPortPhotoFile = useCallback((file) => {
    clearPortPhotoPreview();
    setNewPortPhotoFile(file || null);
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      portPhotoPreviewObjectUrlRef.current = previewUrl;
      setNewPortPhotoPreviewUrl(previewUrl);
    }
  }, [clearPortPhotoPreview]);

  const selectPortPhotoFile = useCallback((file) => {
    if (!file) {
      closePortPhotoCropModal();
      setCroppedPortPhotoFile(null);
      return;
    }

    clearPortPhotoCropSource();
    const sourceUrl = URL.createObjectURL(file);
    portPhotoCropObjectUrlRef.current = sourceUrl;
    setPortPhotoCropFile(file);
    setPortPhotoCropSourceUrl(sourceUrl);
    setPortPhotoCropImageMeta(null);
    setPortPhotoCropZoom(1);
    setPortPhotoCropOffset({ x: 0, y: 0 });
    setPortPhotoCropModalOpen(true);
  }, [clearPortPhotoCropSource, closePortPhotoCropModal, setCroppedPortPhotoFile]);

  useEffect(() => () => {
    if (portPhotoPreviewObjectUrlRef.current) {
      URL.revokeObjectURL(portPhotoPreviewObjectUrlRef.current);
      portPhotoPreviewObjectUrlRef.current = null;
    }
    if (portPhotoCropObjectUrlRef.current) {
      URL.revokeObjectURL(portPhotoCropObjectUrlRef.current);
      portPhotoCropObjectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!portPhotoCropModalOpen || !portPhotoCropFrameRef.current) return undefined;

    const updateFrameSize = () => {
      const rect = portPhotoCropFrameRef.current?.getBoundingClientRect();
      if (!rect?.width || !rect?.height) return;
      setPortPhotoCropFrameSize({
        width: rect.width,
        height: rect.height,
      });
    };

    updateFrameSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateFrameSize);
      return () => window.removeEventListener("resize", updateFrameSize);
    }

    const observer = new ResizeObserver(updateFrameSize);
    observer.observe(portPhotoCropFrameRef.current);
    return () => observer.disconnect();
  }, [portPhotoCropModalOpen]);

  useEffect(() => {
    if (!portPhotoCropModalOpen) return;
    setPortPhotoCropOffset((offset) => clampCropOffset(offset, portPhotoCropImageMeta, portPhotoCropFrameSize, portPhotoCropZoom));
  }, [portPhotoCropFrameSize, portPhotoCropImageMeta, portPhotoCropModalOpen, portPhotoCropZoom]);

  const handlePortPhotoCropImageLoad = useCallback((event) => {
    const image = event.currentTarget;
    setPortPhotoCropImageMeta({
      width: image.naturalWidth,
      height: image.naturalHeight,
    });
    setPortPhotoCropOffset({ x: 0, y: 0 });
    setPortPhotoCropZoom(1);
  }, []);

  const updatePortPhotoCropZoom = useCallback((value) => {
    const nextZoom = Math.min(3, Math.max(1, Number(value) || 1));
    setPortPhotoCropZoom(nextZoom);
    setPortPhotoCropOffset((offset) => clampCropOffset(offset, portPhotoCropImageMeta, portPhotoCropFrameSize, nextZoom));
  }, [portPhotoCropFrameSize, portPhotoCropImageMeta]);

  const startPortPhotoCropDrag = useCallback((event) => {
    if (!portPhotoCropImageMeta || !portPhotoCropFrameSize.width) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    portPhotoCropDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offset: portPhotoCropOffset,
    };
  }, [portPhotoCropFrameSize.width, portPhotoCropImageMeta, portPhotoCropOffset]);

  const movePortPhotoCropDrag = useCallback((event) => {
    const drag = portPhotoCropDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const nextOffset = {
      x: drag.offset.x + event.clientX - drag.startX,
      y: drag.offset.y + event.clientY - drag.startY,
    };
    setPortPhotoCropOffset(clampCropOffset(nextOffset, portPhotoCropImageMeta, portPhotoCropFrameSize, portPhotoCropZoom));
  }, [portPhotoCropFrameSize, portPhotoCropImageMeta, portPhotoCropZoom]);

  const endPortPhotoCropDrag = useCallback((event) => {
    if (portPhotoCropDragRef.current?.pointerId === event.pointerId) {
      portPhotoCropDragRef.current = null;
    }
  }, []);

  const applyPortPhotoCrop = useCallback(async () => {
    if (!portPhotoCropFile || !portPhotoCropSourceUrl || !portPhotoCropImageMeta || !portPhotoCropFrameSize.width || !portPhotoCropFrameSize.height) {
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = PORT_CARD_PHOTO_OUTPUT_WIDTH;
    canvas.height = PORT_CARD_PHOTO_OUTPUT_HEIGHT;
    const context = canvas.getContext("2d");
    if (!context) return;

    const image = new Image();
    image.src = portPhotoCropSourceUrl;

    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
    });

    const baseDimensions = getCropBaseDimensions(portPhotoCropImageMeta, portPhotoCropFrameSize);
    const scale = (baseDimensions.width * portPhotoCropZoom) / portPhotoCropImageMeta.width;
    const displayWidth = portPhotoCropImageMeta.width * scale;
    const displayHeight = portPhotoCropImageMeta.height * scale;
    const imageLeft = (portPhotoCropFrameSize.width - displayWidth) / 2 + portPhotoCropOffset.x;
    const imageTop = (portPhotoCropFrameSize.height - displayHeight) / 2 + portPhotoCropOffset.y;
    const sourceX = Math.max(0, Math.min(portPhotoCropImageMeta.width, -imageLeft / scale));
    const sourceY = Math.max(0, Math.min(portPhotoCropImageMeta.height, -imageTop / scale));
    const sourceWidth = Math.min(portPhotoCropImageMeta.width - sourceX, portPhotoCropFrameSize.width / scale);
    const sourceHeight = Math.min(portPhotoCropImageMeta.height - sourceY, portPhotoCropFrameSize.height / scale);

    context.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );

    const mimeType = portPhotoCropFile.type && portPhotoCropFile.type !== "image/gif"
      ? portPhotoCropFile.type
      : "image/jpeg";
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, mimeType, 0.92));
    if (!blob) return;

    const baseName = portPhotoCropFile.name.replace(/\.[^.]+$/, "") || "port-photo";
    const extension = mimeType === "image/png" ? "png" : mimeType === "image/webp" ? "webp" : "jpg";
    const croppedFile = new File([blob], `${baseName}-port-card.${extension}`, { type: mimeType });

    setCroppedPortPhotoFile(croppedFile);
    closePortPhotoCropModal();
  }, [
    closePortPhotoCropModal,
    portPhotoCropFile,
    portPhotoCropFrameSize,
    portPhotoCropImageMeta,
    portPhotoCropOffset,
    portPhotoCropSourceUrl,
    portPhotoCropZoom,
    setCroppedPortPhotoFile,
  ]);

  // Reset state when sidebar opens/closes
  useEffect(() => {
    if (showPortDockEditBar) {
      // When sidebar opens, ensure we're in the initial state (no edit mode)
      setEditMode(null);
      setIsEditingPort(false);
      setEditingPortId(null);
      setIsEditingDock(false);
      setEditingDockId(null);
      setNewPortPhotoFile(null);
      setExistingPortPhotoUrl("");
      clearPortPhotoPreview();
      setTempLonLat(null);
      mapCtx?.tempPoint?.remove?.();
    }
  }, [showPortDockEditBar, mapCtx, clearPortPhotoPreview]);

  // Haversine distance calculation
  const calculateDistance = (lon1, lat1, lon2, lat2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Fetch countries
  const fetchCountries = async () => {
    try {
      const response = await axios({
        url: buildApiUrl("countries/"),
        method: "get",
        headers: await getApiHeaders(),
      });
      setCountries(response.data);
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  // Fetch timezones
  const fetchTimezones = () => {
    try {
      if (typeof Intl !== 'undefined' && Intl.supportedValuesOf) {
        const supportedTimezones = Intl.supportedValuesOf('timeZone');
        setTimezones(supportedTimezones);
      } else {
        const commonTimezones = [
          'UTC', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome',
          'Europe/Madrid', 'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Vienna',
          'Europe/Prague', 'Europe/Warsaw', 'Europe/Bucharest', 'Europe/Sofia',
          'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Asia/Tokyo'
        ];
        setTimezones(commonTimezones);
      }
    } catch (error) {
      console.error("Error fetching timezones:", error);
      setTimezones(['UTC', 'Europe/London', 'America/New_York']);
    }
  };

  // Coordinate lookup
  const lookupCoordinates = async (longitude, latitude) => {
    console.log(`Looking up coordinates: [${longitude}, ${latitude}]`);
    setIsLookingUpCoordinates(true);

    try {
      const response = await axios({
        url: buildApiUrl("coordinate_lookup/"),
        method: "post",
        data: { latitude, longitude },
        headers: await getApiHeaders(),
      });

      if (response.data.country_code) {
        setNewPortCountry(response.data.country_code);
      }
      if (response.data.timezone) {
        setNewPortTimezone(response.data.timezone);
      }
    } catch (error) {
      console.error("Error looking up coordinates:", error);
    } finally {
      setIsLookingUpCoordinates(false);
    }
  };

  // Fetch available ports for docking locations
  const fetchAvailablePorts = useCallback(async (force = false) => {
    if (!force && availablePortsLoadedRef.current) return;

    try {
      const response = await axios({
        url: buildApiUrl("tour/available_ports_of_call/"),
        method: "get",
        params: { _ts: Date.now() },
        headers: await getApiHeaders(),
      });
      setAvailablePorts(Array.isArray(response.data) ? response.data : []);
      availablePortsLoadedRef.current = true;
    } catch (error) {
      availablePortsLoadedRef.current = false;
      console.error("Error fetching available ports:", error);
    }
  }, [getApiHeaders]);

  // Fetch docking types
  const fetchDockingTypes = useCallback(async (force = false) => {
    if (!force && dockingTypesLoadedRef.current) return;

    try {
      const response = await axios({
        url: buildApiUrl("docking_point_types/"),
        method: "get",
        params: { _ts: Date.now() },
        headers: await getApiHeaders(),
      });
      setDockingTypes(Array.isArray(response.data) ? response.data : []);
      dockingTypesLoadedRef.current = true;
    } catch (error) {
      dockingTypesLoadedRef.current = false;
      console.error("Error fetching docking types:", error);
    }
  }, [getApiHeaders]);

  // Fetch existing ports for browse/edit mode.
  const fetchExistingPorts = useCallback(async () => {
    if (!canViewPorts) return;

    const headers = await getApiHeaders();
    if (!headers.Authorization) return;

    try {
      const response = await axios({
        url: buildApiUrl("ports_of_call_lite/"),
        method: "get",
        params: { _ts: Date.now() },
        headers,
      });
      setAllPorts(response?.data || []);
    } catch (error) {
      console.error("Error fetching ports:", error);
    }
  }, [canViewPorts, getApiHeaders]);

  useEffect(() => {
    if (showPortDockEditBar && canViewPorts) {
      fetchExistingPorts();
    }
  }, [showPortDockEditBar, canViewPorts, fetchExistingPorts]);

  // Display existing ports as markers on the map. Dock markers come from the main docks layer.
  useEffect(() => {
    if (!mapCtx?.addMappoints || !showPortDockEditBar || editMode) return;

    // Clear existing editor markers when in browse mode.
    mapCtx?.clearLayer?.('markers');

    // Convert ports to mappoints format.
    const portMappoints = allPorts.map(port => ({
      id: `port-${port.id}`,
      osm_id: `port-${port.id}`,
      number: '',
      name: `⚓ ${port.name}`,
      center: {
        type: "Point",
        coordinates: port.location?.coordinates || [0, 0]
      }
    }));

    if (portMappoints.length > 0) {
      mapCtx.addMappoints(portMappoints);
    }

    return () => {
      // Cleanup markers when entering edit mode or closing sidebar.
      mapCtx?.clearLayer?.('markers');
    };
  }, [allPorts, showPortDockEditBar, editMode, mapCtx]);

  // Fetch river kilometer for dock location
  const fetchRiverKilometerForDock = useCallback(async (longitude, latitude) => {
    try {
      const res = await axios.post(
        buildApiUrl("nearest_river_marker/"),
        { longitude, latitude },
        { headers: await getApiHeaders() }
      );
      if (res.data && res.data.river_km !== undefined) {
        setNewDockRiverKm(res.data.river_km.toString());
      }
    } catch (e) {
      console.error("Error fetching river kilometer for dock:", e);
    }
  }, [getApiHeaders]);

  const showEditPermissionError = useCallback((resourceLabel = "locations") => {
    setFeedbackMessage(`Your subscription does not allow editing ${resourceLabel}.`);
    setFeedbackType("error");
    setFeedbackModalOpen(true);
  }, []);

  // Start editing existing port
  const startEditingPort = useCallback((portData) => {
    if (!canEditPorts) {
      showEditPermissionError("ports");
      return;
    }
    setEditMode('port');
    setIsEditingPort(true);
    setEditingPortId(portData.id);
    setNewPortName(portData.name || "");
    setNewPortCountry(portData.country?.iso3_code || portData.country_iso3 || "");
    setNewPortTimezone(portData.timezone || "Europe/Vienna");
    setNewPortSupportedTransportModes(normalizeSupportedTransportModes(portData.supported_transport_modes));
    setNewPortPhotoFile(null);
    setExistingPortPhotoUrl(portData.photo_url || portData.photo || "");
    clearPortPhotoPreview();
    setNearbyPorts([]);
    setShowNearbyPortsModal(false);

    // Get coordinates from port location
    const coord = portData.location?.coordinates || [0, 0]; // [lon, lat]
    mapCtx?.tempPoint?.remove?.();
    mapCtx?.tempPoint?.createAt?.(coord);
    setTempPointPosition(coord);

    // Enable drag with coordinate lookup
    mapCtx?.tempPoint?.enableDrag?.((lonlat) => {
      setTempPointPosition(lonlat);
      const [lon, lat] = lonlat;
      lookupCoordinates(lon, lat);
    });

    fetchCountries();
    fetchTimezones();
  }, [mapCtx, canEditPorts, showEditPermissionError, setTempPointPosition, clearPortPhotoPreview]);

  // Start editing existing dock
  const startEditingDock = useCallback((dockData) => {
    if (!canEditDocks) {
      showEditPermissionError("docks");
      return;
    }
    setEditMode('dock');
    setIsEditingDock(true);
    setEditingDockId(dockData.id);
    setNewDockName(dockData.name || "");
    setNewDockPort(dockData.port_of_call?.id || "");
    setNewDockType(dockData.docking_type?.id || "");
    setNewDockRiverKm(dockData.river_km || "");
    setNewDockNotes(dockData.notes || "");

    const coord = llToLonLat(dockData.coord);
    mapCtx?.tempPoint?.remove?.();
    mapCtx?.tempPoint?.createAt?.(coord);
    setTempPointPosition(coord);

    // Enable drag with callback to fetch river kilometer
    mapCtx?.tempPoint?.enableDrag?.((lonlat) => {
      setTempPointPosition(lonlat);
      const [lon, lat] = lonlat;
      fetchRiverKilometerForDock(lon, lat);
    });

    fetchAvailablePorts();
    fetchDockingTypes();

    // Optionally fetch river kilometer if not set
    if (!dockData.river_km && coord) {
      setTimeout(() => {
        const [lon, lat] = coord;
        fetchRiverKilometerForDock(lon, lat);
      }, 100);
    }
  }, [mapCtx, fetchRiverKilometerForDock, canEditDocks, showEditPermissionError, setTempPointPosition]);

  useEffect(() => {
    if (!showPortDockEditBar || !externalDockEditRequest?.id) return;

    const requestId = externalDockEditRequest.requestId || String(externalDockEditRequest.id);
    if (handledExternalDockEditRequestRef.current === requestId) return;

    handledExternalDockEditRequestRef.current = requestId;
    startEditingDock(externalDockEditRequest);
    onExternalDockEditRequestHandled?.();
  }, [
    externalDockEditRequest,
    onExternalDockEditRequestHandled,
    showPortDockEditBar,
    startEditingDock,
  ]);

  // Handle port marker clicks to edit existing ports.
  useEffect(() => {
    if (!mapCtx?.onMarkerClick || !showPortDockEditBar || editMode || !canEditPorts) return;

    const unsubscribe = mapCtx.onMarkerClick((markerId, feature) => {
      console.log('Port/Dock marker clicked:', markerId, feature);

      if (!markerId) return;

      // Extract the ID without the 'mp-' prefix
      const id = markerId.replace('mp-', '');

      // Determine if it's a port or dock
      if (id.startsWith('port-')) {
        const portId = parseInt(id.replace('port-', ''));
        const port = allPorts.find(p => p.id === portId);
        if (port) {
          startEditingPort(port);
        }
      }
    });

    return unsubscribe;
  }, [mapCtx, showPortDockEditBar, editMode, allPorts, startEditingPort, canEditPorts]);

  // Start creating a new port
  const startCreatingPort = useCallback(() => {
    if (!canEditPorts) {
      showEditPermissionError("ports");
      return;
    }
    setEditMode('port');
    setIsEditingPort(false);
    setEditingPortId(null);
    setNewPortName("");
    setNewPortCountry("");
    setNewPortTimezone("Europe/Vienna");
    setNewPortSupportedTransportModes(["road"]);
    setNewPortPhotoFile(null);
    setExistingPortPhotoUrl("");
    clearPortPhotoPreview();
    setNearbyPorts([]);
    setShowNearbyPortsModal(false);

    // Create marker at center
    mapCtx?.tempPoint?.remove?.();
    mapCtx?.tempPoint?.createAtCenter?.();

    // Enable drag with coordinate lookup
    mapCtx?.tempPoint?.enableDrag?.((lonlat) => {
      setTempPointPosition(lonlat);
      const [lon, lat] = lonlat;
      lookupCoordinates(lon, lat);
    });

    // Initial lookup
    const initialCoords = mapCtx?.tempPoint?.getLonLat?.();
    if (initialCoords) {
      setTempPointPosition(initialCoords);
      const [lon, lat] = initialCoords;
      lookupCoordinates(lon, lat);
    }

    fetchCountries();
    fetchTimezones();
  }, [mapCtx, canEditPorts, showEditPermissionError, setTempPointPosition, clearPortPhotoPreview]);

  // Start creating a new dock
  const startCreatingDock = useCallback(() => {
    if (!canEditDocks) {
      showEditPermissionError("docks");
      return;
    }
    setEditMode('dock');
    setIsEditingDock(false);
    setEditingDockId(null);
    setNewDockName("");
    setNewDockPort("");
    setNewDockType("");
    setNewDockRiverKm("");
    setNewDockNotes("");

    // Create marker at center
    mapCtx?.tempPoint?.remove?.();
    mapCtx?.tempPoint?.createAtCenter?.();

    // Enable drag with callback to fetch river kilometer
    mapCtx?.tempPoint?.enableDrag?.((lonlat) => {
      setTempPointPosition(lonlat);
      const [lon, lat] = lonlat;
      fetchRiverKilometerForDock(lon, lat);
    });

    fetchAvailablePorts();
    fetchDockingTypes();

    // Fetch initial river kilometer for the center position
    setTimeout(() => {
      const initialCoords = mapCtx?.tempPoint?.getLonLat?.();
      if (initialCoords) {
        setTempPointPosition(initialCoords);
        const [lon, lat] = initialCoords;
        fetchRiverKilometerForDock(lon, lat);
      }
    }, 100);
  }, [mapCtx, fetchRiverKilometerForDock, canEditDocks, showEditPermissionError, setTempPointPosition]);

  // Create new port
  const createNewPort = async (forceCreate = false) => {
    if (!canEditPorts) {
      showEditPermissionError("ports");
      return;
    }

    if (!newPortName.trim()) {
      setFeedbackMessage("Please enter a port name.");
      setFeedbackType("error");
      setFeedbackModalOpen(true);
      return;
    }

    if (!newPortSupportedTransportModes.length) {
      setFeedbackMessage("Please select at least one supported transport mode.");
      setFeedbackType("error");
      setFeedbackModalOpen(true);
      return;
    }

    const ll = tempLonLat || mapCtx?.tempPoint?.getLonLat?.();
    if (!ll) {
      setFeedbackMessage("Please set a location for the port on the map.");
      setFeedbackType("error");
      setFeedbackModalOpen(true);
      return;
    }

    const [lon, lat] = ll;

    if (!forceCreate) {
      try {
        const response = await axios({
          url: buildApiUrl("ports_of_call_lite/"),
          method: "get",
          params: { _ts: Date.now() },
          headers: await getApiHeaders(),
        });

        const allPorts = response.data || [];

        // When editing, exclude the current port from duplicate/nearby checks
        const portsToCheck = isEditingPort && editingPortId
          ? allPorts.filter(port => port.id !== editingPortId)
          : allPorts;

        const existingPort = portsToCheck.find(port =>
          port.name && port.name.toLowerCase().trim() === newPortName.toLowerCase().trim()
        );

        if (existingPort) {
          let duplicateWithDistance = { ...existingPort };
          if (existingPort.location && existingPort.location.coordinates) {
            const [portLon, portLat] = existingPort.location.coordinates;
            const distance = calculateDistance(lon, lat, portLon, portLat);
            duplicateWithDistance.distance = distance.toFixed(2);
          } else {
            duplicateWithDistance.distance = "Unknown";
          }

          setNearbyPorts([duplicateWithDistance]);
          setShowNearbyPortsModal(true);
          return;
        }

        const nearby = portsToCheck.filter(port => {
          if (port.location && port.location.coordinates) {
            const [portLon, portLat] = port.location.coordinates;
            const distance = calculateDistance(lon, lat, portLon, portLat);
            return distance <= 20;
          }
          return false;
        }).map(port => {
          const [portLon, portLat] = port.location.coordinates;
          const distance = calculateDistance(lon, lat, portLon, portLat);
          return { ...port, distance: distance.toFixed(2) };
        }).sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));

        if (nearby.length > 0) {
          setNearbyPorts(nearby);
          setShowNearbyPortsModal(true);
          return;
        }
      } catch (error) {
        console.error("Error checking for existing ports:", error);
      }
    }

    setIsSaving(true);
    try {
      let selectedCountry = null;

      if (newPortCountry) {
        selectedCountry = countries.find(c => c.iso3_code === newPortCountry);
      }

      if (!selectedCountry && countries.length > 0) {
        selectedCountry = countries.find(c => c.iso3_code === 'UNK') || countries[0];
      }

      if (!selectedCountry) {
        setFeedbackMessage("Please select a country for the port.");
        setFeedbackType("error");
        setFeedbackModalOpen(true);
        setIsSaving(false);
        return;
      }

      const data = {
        name: newPortName.trim(),
        country: { iso3_code: selectedCountry.iso3_code },
        timezone: newPortTimezone || "Europe/Vienna",
        location: { type: "Point", coordinates: [lon, lat] },
        notes: selectedCountry.name ? `Country: ${selectedCountry.name}` : "",
        supported_transport_modes: normalizeSupportedTransportModes(newPortSupportedTransportModes),
      };
      let requestData = data;
      const headers = await getApiHeaders();

      if (newPortPhotoFile) {
        const formData = new FormData();
        formData.append("name", data.name);
        formData.append("country_iso3", selectedCountry.iso3_code);
        formData.append("timezone", data.timezone);
        formData.append("location", JSON.stringify(data.location));
        formData.append("notes", data.notes);
        formData.append("supported_transport_modes", JSON.stringify(data.supported_transport_modes));
        formData.append("photo", newPortPhotoFile);
        requestData = formData;
      }

      const response = await axios({
        url: buildApiUrl(isEditingPort && editingPortId ? `ports_of_call/${editingPortId}/` : "ports_of_call/"),
        method: isEditingPort && editingPortId ? "put" : "post",
        data: requestData,
        headers,
      });

      setFeedbackMessage(`Port "${response.data.name}" ${isEditingPort ? 'updated' : 'created'} successfully!`);
      setFeedbackType("success");
      setFeedbackModalOpen(true);

      // Notify parent and refresh the list
      if (onPortCreated) {
        onPortCreated(response.data);
      }

      await Promise.all([
        fetchExistingPorts(),
        fetchAvailablePorts(true),
      ]);

      // Reset form
      cancelEdit();

    } catch (error) {
      console.error("Error creating new port:", error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || "Failed to create new port.";
      setFeedbackMessage(errorMsg);
      setFeedbackType("error");
      setFeedbackModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Save docking location
  const saveDockingLocation = async () => {
    if (!canEditDocks) {
      showEditPermissionError("docks");
      return;
    }

    if (!newDockName.trim()) {
      setFeedbackMessage("Please enter a docking location name.");
      setFeedbackType("error");
      setFeedbackModalOpen(true);
      return;
    }

    if (!newDockPort) {
      setFeedbackMessage("Please select a port of call.");
      setFeedbackType("error");
      setFeedbackModalOpen(true);
      return;
    }

    if (!newDockType) {
      setFeedbackMessage("Please select a docking type.");
      setFeedbackType("error");
      setFeedbackModalOpen(true);
      return;
    }

    const ll = tempLonLat || mapCtx?.tempPoint?.getLonLat?.();
    if (!ll) {
      setFeedbackMessage("Please set a location on the map.");
      setFeedbackType("error");
      setFeedbackModalOpen(true);
      return;
    }

    const [lon, lat] = ll;

    setIsSaving(true);
    try {
      const data = {
        name: newDockName.trim(),
        port_of_call_id: newDockPort,
        docking_type_id: newDockType,
        river_km: newDockRiverKm ? parseFloat(newDockRiverKm) : null,
        notes: newDockNotes.trim() || null,
        location: { type: "Point", coordinates: [lon, lat] }
      };

      if (isEditingDock && editingDockId) {
        await axios({
          url: buildApiUrl(`dockingpoints/${editingDockId}/`),
          method: "put",
          data,
          headers: await getApiHeaders(),
        });
        setFeedbackMessage("Docking location updated successfully!");
      } else {
        await axios({
          url: buildApiUrl("dockingpoints/"),
          method: "post",
          data,
          headers: await getApiHeaders(),
        });
        setFeedbackMessage("Docking location created successfully!");
      }

      setFeedbackType("success");
      setFeedbackModalOpen(true);

      // Notify parent
      if (onDockCreated) {
        onDockCreated();
      }

      // Reset form
      cancelEdit();

    } catch (error) {
      console.error("Error saving docking location:", error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || "Failed to save docking location.";
      setFeedbackMessage(errorMsg);
      setFeedbackType("error");
      setFeedbackModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Delete port
  const deletePort = async () => {
    if (!canEditPorts) {
      showEditPermissionError("ports");
      return;
    }

    if (!editingPortId) return;

    const confirmed = window.confirm("Are you sure you want to delete this port?");
    if (!confirmed) return;

    setIsSaving(true);
    try {
      await axios({
        url: buildApiUrl(`ports_of_call/${editingPortId}/`),
        method: "delete",
        headers: await getApiHeaders(),
      });

      setFeedbackMessage("Port deleted successfully!");
      setFeedbackType("success");
      setFeedbackModalOpen(true);

      // Notify parent
      if (onPortCreated) {
        onPortCreated();
      }

      await Promise.all([
        fetchExistingPorts(),
        fetchAvailablePorts(true),
      ]);

      // Reset form
      cancelEdit();

    } catch (error) {
      console.error("Error deleting port:", error);
      setFeedbackMessage("Failed to delete port.");
      setFeedbackType("error");
      setFeedbackModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Delete docking location
  const deleteDockingLocation = async () => {
    if (!canEditDocks) {
      showEditPermissionError("docks");
      return;
    }

    if (!editingDockId) return;

    const confirmed = window.confirm("Are you sure you want to delete this docking location?");
    if (!confirmed) return;

    setIsSaving(true);
    try {
      await axios({
        url: buildApiUrl(`dockingpoints/${editingDockId}/`),
        method: "delete",
        headers: await getApiHeaders(),
      });

      setFeedbackMessage("Docking location deleted successfully!");
      setFeedbackType("success");
      setFeedbackModalOpen(true);

      // Notify parent
      if (onDockCreated) {
        onDockCreated();
      }

      // Reset form
      cancelEdit();

    } catch (error) {
      console.error("Error deleting docking location:", error);
      setFeedbackMessage("Failed to delete docking location.");
      setFeedbackType("error");
      setFeedbackModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel editing
  const cancelEdit = useCallback(() => {
    mapCtx?.tempPoint?.remove?.();
    setEditMode(null);
    setIsEditingPort(false);
    setEditingPortId(null);
    setIsEditingDock(false);
    setEditingDockId(null);
    setNewPortName("");
    setNewPortCountry("");
    setNewPortTimezone("Europe/Vienna");
    setNewPortSupportedTransportModes(["road"]);
    setNewPortPhotoFile(null);
    setExistingPortPhotoUrl("");
    clearPortPhotoPreview();
    setNewDockName("");
    setNewDockPort("");
    setNewDockType("");
    setNewDockRiverKm("");
    setNewDockNotes("");
    setTempLonLat(null);
    setNearbyPorts([]);
    setShowNearbyPortsModal(false);
  }, [mapCtx, clearPortPhotoPreview]);

  useEffect(() => {
    if (showPortDockEditBar && editMode && !canEditPortsDocks) {
      cancelEdit();
    }
  }, [showPortDockEditBar, editMode, canEditPortsDocks, cancelEdit]);

  // Close sidebar
  const closeSidebar = () => {
    if (editMode) {
      cancelEdit();
      return;
    }
    setShowPortDockEditBar(false);
  };

  // Proceed with creating port despite nearby warnings
  const proceedWithNewPort = () => {
    setShowNearbyPortsModal(false);
    setNearbyPorts([]);
    createNewPort(true);
  };

  const activePortPhotoPreviewUrl = newPortPhotoPreviewUrl || existingPortPhotoUrl;
  const portPhotoCropBaseDimensions = getCropBaseDimensions(portPhotoCropImageMeta, portPhotoCropFrameSize);
  const portPhotoCropDisplayWidth = portPhotoCropBaseDimensions.width * portPhotoCropZoom;
  const portPhotoCropDisplayHeight = portPhotoCropBaseDimensions.height * portPhotoCropZoom;

  return (
    <SidebarWrapper
      isMobile={isMobile}
      sidebarConfig={sidebarConfig}
      onClose={closeSidebar}
      onHeightChange={onHeightChange}
      zIndex={505}
    >
      {/* Header */}
      <div
        style={{
          padding: isMobile ? "10px 8px" : "15px",
          borderBottom: "1px solid #ddd",
          background: "#f8f9fa",
        }}
      >
        <Header as="h3" style={{ margin: 0 }}>
          <Icon name={editMode === 'port' ? "map pin" : "anchor"} />
          {editMode === 'port' && "Create Port of Call"}
          {editMode === 'dock' && (isEditingDock ? "Edit Docking Location" : "Create Docking Location")}
          {!editMode && "Port & Dock Editor"}
        </Header>
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: isMobile ? "10px 8px" : "15px",
        }}
      >
        {!canViewPortsDocks && !editMode && (
          <div style={{ textAlign: "center", padding: "32px 20px", color: "#666" }}>
            <p>You do not have access to view ports or docks.</p>
          </div>
        )}

        {canViewPortsDocks && !editMode && (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <p style={{ marginBottom: "20px", color: "#666" }}>
              {canEditPortsDocks
                ? "Choose what you want to create:"
                : "Browse available ports on the map. Docking locations are edited from the Docks layer modal."}
            </p>
            <Button
              color="blue"
              size="large"
              icon="map pin"
              content="Create New Port"
              onClick={startCreatingPort}
              disabled={!canEditPorts}
              style={{ marginBottom: "15px", width: "100%" }}
            />
            <Button
              color="teal"
              size="large"
              icon="anchor"
              content="Create New Docking Location"
              onClick={startCreatingDock}
              disabled={!canEditDocks}
              style={{ width: "100%" }}
            />
          </div>
        )}

        {/* Port Creation Form */}
        {editMode === 'port' && (
          <Form>
            <Form.Field required>
              <label>Port Name</label>
              <Input
                placeholder="Enter port name"
                value={newPortName}
                onChange={(e) => setNewPortName(e.target.value)}
              />
            </Form.Field>
            <Form.Field>
              <label>Country</label>
              <Dropdown
                placeholder="Select country"
                fluid
                search
                selection
                loading={isLookingUpCoordinates}
                options={countryOptions}
                value={newPortCountry}
                onChange={(e, { value }) => setNewPortCountry(value)}
              />
            </Form.Field>
            <Form.Field>
              <label>Timezone</label>
              <Dropdown
                placeholder="Select timezone"
                fluid
                search
                selection
                options={timezoneOptions}
                value={newPortTimezone}
                onChange={(e, { value }) => setNewPortTimezone(value)}
              />
            </Form.Field>
            <Form.Field required>
              <label>Supported Transport</label>
              <Dropdown
                placeholder="Select supported transport modes"
                fluid
                multiple
                selection
                options={SUPPORTED_TRANSPORT_OPTIONS.map((option) => ({
                  ...option,
                  content: (
                    <span>
                      <Icon name={option.icon} /> {option.text}
                    </span>
                  ),
                }))}
                value={newPortSupportedTransportModes}
                onChange={(e, { value }) => {
                  setNewPortSupportedTransportModes(normalizeSupportedTransportModes(value));
                }}
              />
              <div style={{ marginTop: "6px", fontSize: "0.85em", color: "#666" }}>
                Road is the baseline. Add river or sea when the port supports those transport families.
              </div>
            </Form.Field>
            <Form.Field>
              <label>Port Photo</label>
              {activePortPhotoPreviewUrl && (
                <div
                  style={{
                    width: "100%",
                    aspectRatio: `${PORT_CARD_PHOTO_ASPECT_RATIO}`,
                    marginBottom: "10px",
                    borderRadius: "4px",
                    overflow: "hidden",
                    border: "1px solid #d4d4d5",
                    background: "#f8f9fa",
                  }}
                >
                  <img
                    src={activePortPhotoPreviewUrl}
                    alt={newPortName ? `${newPortName} port` : "Port"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>
              )}
              <input
                ref={portPhotoFileInputRef}
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  selectPortPhotoFile(file);
                }}
                disabled={isSaving}
              />
              {newPortPhotoFile && (
                <Button
                  type="button"
                  size="tiny"
                  basic
                  icon
                  labelPosition="left"
                  onClick={() => selectPortPhotoFile(newPortPhotoFile)}
                  disabled={isSaving}
                  style={{ marginTop: "8px" }}
                >
                  <Icon name="crop" />
                  Adjust Crop
                </Button>
              )}
              <div style={{ marginTop: "6px", fontSize: "0.85em", color: "#666" }}>
                Upload one image that best represents this port. You can crop it to the mobile port card ratio before saving.
              </div>
            </Form.Field>
            <div style={{ marginTop: "15px", padding: "12px", background: "#e8f4f8", borderRadius: "4px", border: "1px solid #bee5eb" }}>
              <p style={{ margin: 0, fontSize: "0.9em" }}><strong>Instructions:</strong></p>
              <p style={{ margin: "8px 0 0 0", fontSize: "0.85em" }}>
                1. Drag the <span style={{ color: "#ff6b00", fontWeight: "bold" }}>orange marker</span> on the map to set the port location<br/>
                2. Country and timezone will auto-fill based on location<br/>
                3. Choose the supported transport families for this port<br/>
                4. Optionally upload a representative port photo<br/>
                5. Click "{isEditingPort ? "Update Port" : "Create Port"}" to save
              </p>
            </div>
          </Form>
        )}

        {/* Docking Location Form */}
        {editMode === 'dock' && (
          <Form>
            <Form.Field required>
              <label>Docking Location Name</label>
              <Input
                placeholder="e.g., Passenger Terminal 1"
                value={newDockName}
                onChange={(e) => setNewDockName(e.target.value)}
              />
            </Form.Field>
            <Form.Field required>
              <label>Port of Call</label>
              <Dropdown
                placeholder="Select port of call"
                fluid
                search
                selection
                options={availablePortOptions}
                value={newDockPort}
                onChange={(e, { value }) => setNewDockPort(value)}
              />
            </Form.Field>
            <Form.Field required>
              <label>Docking Type</label>
              <Dropdown
                placeholder="Select docking type"
                fluid
                selection
                options={dockingTypeOptions}
                value={newDockType}
                onChange={(e, { value }) => setNewDockType(value)}
              />
            </Form.Field>
            <Form.Field>
              <label>River KM</label>
              <Input
                type="number"
                step="0.1"
                placeholder="e.g., 642.5"
                value={newDockRiverKm}
                onChange={(e) => setNewDockRiverKm(e.target.value)}
              />
            </Form.Field>
            <Form.Field>
              <label>Notes</label>
              <Form.TextArea
                placeholder="Additional notes about this docking location"
                value={newDockNotes}
                onChange={(e) => setNewDockNotes(e.target.value)}
                rows={3}
              />
            </Form.Field>
            <div style={{ marginTop: "15px", padding: "12px", background: "#e8f4f8", borderRadius: "4px", border: "1px solid #bee5eb" }}>
              <p style={{ margin: 0, fontSize: "0.9em" }}><strong>Instructions:</strong></p>
              <p style={{ margin: "8px 0 0 0", fontSize: "0.85em" }}>
                1. Drag the <span style={{ color: "#ff6b00", fontWeight: "bold" }}>orange marker</span> on the map to set the exact location<br/>
                2. Fill in all required fields<br/>
                3. Click "Save" to create/update the docking location
              </p>
            </div>
          </Form>
        )}
      </div>

      {/* Action Buttons */}
      {editMode && (
        <div
          style={{
            padding: isMobile ? "10px 8px" : "15px",
            borderTop: "1px solid #ddd",
            background: "#f8f9fa",
          }}
        >
          <Grid>
            <Grid.Row>
              <Grid.Column width={16}>
                {editMode === 'port' && isEditingPort && (
                  <Button
                    color="red"
                    onClick={deletePort}
                    loading={isSaving}
                    disabled={isSaving || !canEditPorts}
                    style={{ marginBottom: "10px", width: "100%" }}
                  >
                    <Icon name="trash" /> Delete Port
                  </Button>
                )}
                {editMode === 'dock' && isEditingDock && (
                  <Button
                    color="red"
                    onClick={deleteDockingLocation}
                    loading={isSaving}
                    disabled={isSaving || !canEditDocks}
                    style={{ marginBottom: "10px", width: "100%" }}
                  >
                    <Icon name="trash" /> Delete Docking Location
                  </Button>
                )}
                <Button.Group widths={(editMode === "port" ? canEditPorts : canEditDocks) ? "2" : "1"}>
                  <Button onClick={cancelEdit} disabled={isSaving}>
                    <Icon name="cancel" /> Cancel
                  </Button>
                  {(editMode === "port" ? canEditPorts : canEditDocks) && (
                    <Button
                      color="green"
                      onClick={editMode === 'port' ? () => createNewPort(false) : saveDockingLocation}
                      loading={isSaving}
                      disabled={isSaving}
                    >
                      <Icon name="checkmark" /> {editMode === 'port' ? (isEditingPort ? 'Update Port' : 'Create Port') : (isEditingDock ? 'Update' : 'Save')}
                    </Button>
                  )}
                </Button.Group>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      )}

      {/* Port Photo Crop Modal */}
      <Modal
        open={portPhotoCropModalOpen}
        onClose={closePortPhotoCropModal}
        size="small"
        closeOnDimmerClick={false}
      >
        <Header icon="crop" content="Crop Port Photo" />
        <Modal.Content>
          <div
            ref={portPhotoCropFrameRef}
            onPointerDown={startPortPhotoCropDrag}
            onPointerMove={movePortPhotoCropDrag}
            onPointerUp={endPortPhotoCropDrag}
            onPointerCancel={endPortPhotoCropDrag}
            style={{
              position: "relative",
              width: "100%",
              aspectRatio: `${PORT_CARD_PHOTO_ASPECT_RATIO}`,
              maxHeight: isMobile ? "42vh" : "360px",
              overflow: "hidden",
              borderRadius: "4px",
              border: "1px solid #d4d4d5",
              background: "#111827",
              cursor: portPhotoCropImageMeta ? "grab" : "default",
              touchAction: "none",
              userSelect: "none",
            }}
          >
            {portPhotoCropSourceUrl && (
              <img
                src={portPhotoCropSourceUrl}
                alt="Crop preview"
                onLoad={handlePortPhotoCropImageLoad}
                draggable={false}
                style={{
                  position: "absolute",
                  left: `calc(50% + ${portPhotoCropOffset.x}px)`,
                  top: `calc(50% + ${portPhotoCropOffset.y}px)`,
                  width: `${portPhotoCropDisplayWidth || 0}px`,
                  height: `${portPhotoCropDisplayHeight || 0}px`,
                  maxWidth: "none",
                  transform: "translate(-50%, -50%)",
                  display: portPhotoCropImageMeta ? "block" : "none",
                  pointerEvents: "none",
                }}
              />
            )}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                border: "2px solid rgba(255, 255, 255, 0.9)",
                boxShadow: "inset 0 0 0 9999px rgba(0, 0, 0, 0.08)",
                pointerEvents: "none",
              }}
            />
          </div>
          <Form.Field style={{ marginTop: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: 700 }}>
              Zoom
            </label>
            <input
              type="range"
              min="1"
              max="3"
              step="0.01"
              value={portPhotoCropZoom}
              onChange={(event) => updatePortPhotoCropZoom(event.target.value)}
              disabled={!portPhotoCropImageMeta}
              style={{ width: "100%" }}
            />
          </Form.Field>
          <div style={{ marginTop: "8px", fontSize: "0.85em", color: "#666" }}>
            Drag the image to position it. The crop matches the mobile port card photo ratio.
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closePortPhotoCropModal}>
            <Icon name="cancel" /> Cancel
          </Button>
          <Button
            color="green"
            onClick={applyPortPhotoCrop}
            disabled={!portPhotoCropImageMeta}
          >
            <Icon name="checkmark" /> Use Crop
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Nearby Ports Warning Modal */}
      <Modal
        open={showNearbyPortsModal}
        onClose={() => setShowNearbyPortsModal(false)}
        size="small"
      >
        <Header icon="warning sign" content="Nearby Ports Found" />
        <Modal.Content>
          <p>The following ports already exist near this location:</p>
          <List divided relaxed>
            {nearbyPorts.map((port) => (
              <List.Item key={port.id}>
                <List.Content>
                  <List.Header>{port.name}</List.Header>
                  <List.Description>
                    Distance: {port.distance} km | Country: {port.country_name}
                  </List.Description>
                </List.Content>
              </List.Item>
            ))}
          </List>
          <p style={{ marginTop: "15px" }}>
            Do you still want to create a new port at this location?
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowNearbyPortsModal(false)}>
            <Icon name="cancel" /> Cancel
          </Button>
          <Button color="green" onClick={proceedWithNewPort}>
            <Icon name="checkmark" /> Proceed Anyway
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Feedback Modal */}
      <Modal
        open={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        size="mini"
      >
        <Modal.Content>
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Icon
              name={feedbackType === "success" ? "check circle" : "exclamation triangle"}
              size="huge"
              color={feedbackType === "success" ? "green" : "red"}
            />
            <p style={{ marginTop: "15px", fontSize: "1.1em" }}>{feedbackMessage}</p>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button color="green" onClick={() => setFeedbackModalOpen(false)}>
            <Icon name="checkmark" /> OK
          </Button>
        </Modal.Actions>
      </Modal>
    </SidebarWrapper>
  );
}
